import fs from 'node:fs';
import path from 'node:path';
import { isValidSlug } from './slug.js';
import { hasDatabase, hasFileStorage } from './names.js';

/**
 * app.config.json — the per-app file `ghosty init` writes and every other
 * command reads. Templates may ship a partial app.config.json; the CLI's
 * generated values win on merge.
 */
export interface AppConfig {
  slug: string;
  name: string;
  description: string;
  /**
   * Whether the app has a database. `ghosty init` writes a boolean (apps
   * scaffold with it DISABLED), but the Claude `database-model` agent may
   * flip it to `{ needed: true }` — `readAppConfig` collapses both forms to
   * a canonical boolean via `hasDatabase()`.
   */
  database: boolean;
  /**
   * Whether the app has file storage (uploads, images, documents). Same
   * shape rules as `database`: canonical boolean; the `file-storage` agent
   * flips it and `readAppConfig` collapses `{needed:true}` too.
   */
  fileStorage?: boolean;
  backend: 'node' | 'python';
  /** Who can reach the deployed app; absent on older configs → 'public'. */
  accessMode?: 'public' | 'invite' | 'members' | 'byo';
  /** Placeholder secrets to provision as {slug}-{name}. */
  secrets?: Array<string | { name: string }>;
  /** Non-secret env vars set on the Cloud Run service. */
  env?: Record<string, string>;
  platform: {
    adminUrl: string;
    company: string;
  };
  [key: string]: unknown;
}

export function appConfigPath(dir: string): string {
  return path.join(dir, 'app.config.json');
}

/**
 * Best-effort read of `platform.adminUrl` from app.config.json in `dir`.
 *
 * Returns undefined when the file is absent, unreadable, or carries no admin
 * URL — it NEVER throws (unlike `readAppConfig`), because it's a discovery
 * fallback: a downloaded app folder carries its own admin URL, so a brand-new
 * user with no saved profile can still `ghosty login` from inside it.
 */
export function adminUrlFromAppConfig(dir: string): string | undefined {
  const file = appConfigPath(dir);
  if (!fs.existsSync(file)) return undefined;
  try {
    const parsed = JSON.parse(fs.readFileSync(file, 'utf8')) as Partial<AppConfig>;
    const url = parsed.platform?.adminUrl;
    return typeof url === 'string' && url.length > 0 ? url : undefined;
  } catch {
    return undefined;
  }
}

export function readAppConfig(dir: string): AppConfig {
  const file = appConfigPath(dir);
  if (!fs.existsSync(file)) {
    throw new Error(
      `No app.config.json found in ${dir}. Run \`ghosty init\` first, or pass --dir <app folder>.`,
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(fs.readFileSync(file, 'utf8'));
  } catch (err) {
    throw new Error(`${file} is not valid JSON: ${(err as Error).message}`);
  }
  const cfg = parsed as Partial<AppConfig>;
  if (typeof cfg.slug !== 'string' || !isValidSlug(cfg.slug)) {
    throw new Error(`${file} is missing a valid kebab-case "slug" field.`);
  }
  if (typeof cfg.name !== 'string' || cfg.name.length === 0) {
    throw new Error(`${file} is missing a "name" field.`);
  }
  const full = cfg as AppConfig;
  return {
    ...full,
    description: typeof full.description === 'string' ? full.description : '',
    database: hasDatabase(full.database),
    fileStorage: hasFileStorage(full.fileStorage),
    backend: full.backend === 'python' ? 'python' : 'node',
    platform: full.platform ?? { adminUrl: '', company: '' },
  };
}

/**
 * Write app.config.json, merging over any template-provided partial file
 * already in the directory. CLI-generated fields win.
 */
export function writeAppConfig(dir: string, config: AppConfig): void {
  const file = appConfigPath(dir);
  let existing: Record<string, unknown> = {};
  if (fs.existsSync(file)) {
    try {
      const parsed = JSON.parse(fs.readFileSync(file, 'utf8'));
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        existing = parsed as Record<string, unknown>;
      }
    } catch {
      /* malformed template partial — CLI config replaces it */
    }
  }
  const merged = { ...existing, ...config };
  fs.writeFileSync(file, `${JSON.stringify(merged, null, 2)}\n`);
}

/** Normalize the optional secrets array to the manifest's [{name}] shape. */
export function normalizeSecrets(
  secrets: AppConfig['secrets'],
): Array<{ name: string }> {
  if (!Array.isArray(secrets)) return [];
  return secrets
    .map((s) => (typeof s === 'string' ? { name: s } : s))
    .filter((s): s is { name: string } => Boolean(s && typeof s.name === 'string'));
}

/** Extract the kebab name from a secrets entry (string or {name}). */
function secretEntryName(entry: string | { name: string }): string {
  return typeof entry === 'string' ? entry : entry.name;
}

/**
 * Ensure app.config.json's `secrets` array contains `name` (kebab), deduped.
 * Preserves whatever entry shape (string vs {name}) is already present.
 * Returns the updated config without writing — caller persists via
 * writeAppConfig.
 */
export function addSecretName(config: AppConfig, name: string): AppConfig {
  const existing = Array.isArray(config.secrets) ? config.secrets : [];
  if (existing.some((s) => secretEntryName(s) === name)) {
    return { ...config, secrets: existing };
  }
  return { ...config, secrets: [...existing, name] };
}
